/* eslint-disable no-console */
import queryString from 'query-string';
import type { SessionData } from 'branch-sdk';
import { BranchParsedDataSchema, BranchSeoMetaInfoSchema } from './types';

export interface UTM_PARAMETERS_SCHEMA {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

export interface BranchData extends UTM_PARAMETERS_SCHEMA {
  $sharer_user_id?: string;
  $share_link_id?: string;
  $share_created_date?: string;
  $deeplink: string;
  $deeplink_v2: string;
  $desktop_url?: string;
  $fallback_url?: string;
}

const utmKeys = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term'
] as const;

const BRANCH_ATTRIBUTION_STORAGE_KEY = 'IT-branchAttributionData';
const BRANCH_SEO_META_INFO = 'branch_seo_meta_info';

/**
 * Used to store branch data of url in browser storage. So that we can access it later.
 * storage key is url display address bar and value is branch data.
 * @param data - Branch data from the url.
 * */
export const setBranchDataFromUrl = (data: SessionData | null) => {
  const link = window.location.href;

  if (!window.sessionStorage) {
    return;
  }

  if (data) {
    window.sessionStorage.setItem(link, JSON.stringify(data));
  } else {
    window.sessionStorage.removeItem(link);
  }

  const event = new CustomEvent('storage-change', { detail: { link } });
  window.dispatchEvent(event);
};

/**
 * Return branch link(branch url available in url of browser) data from browser storage.
 * storage key is url display address bar.
 * */
export const getBranchLinkDataFromStorage = () => {
  const link = window.location.href;

  if (!window.sessionStorage) {
    return null;
  }

  const branchData = window.sessionStorage.getItem(link);
  const branchDataFromLink: SessionData | null = branchData
    ? JSON.parse(branchData)
    : null;
  const parsedData: BranchParsedDataSchema | null =
    branchDataFromLink?.data_parsed ?? null;

  return { branchDataFromLink, parsedData };
};

export const saveUTMs = (location: Location, data: SessionData | null) => {
  const data_parsed: Record<string, unknown> | undefined = data?.data_parsed;
  const queries = queryString.parse(location.search);
  const utmData: Record<string, unknown> = {};

  if (queries) {
    if (queries.utm_source) utmData.utm_source = queries.utm_source;
    if (queries.utm_medium) utmData.utm_medium = queries.utm_medium;
    if (queries.utm_campaign) utmData.utm_campaign = queries.utm_campaign;
    if (queries.utm_content) utmData.utm_content = queries.utm_content;
    if (queries.utm_term) utmData.utm_term = queries.utm_term;
  }

  if (data_parsed && Object.keys(data_parsed).length > 0) {
    if (data_parsed['~campaign'])
      utmData.utm_campaign = data_parsed['~campaign'];
    if (data_parsed['~channel']) utmData.utm_source = data_parsed['~channel'];
    if (data_parsed['~feature']) utmData.utm_medium = data_parsed['~feature'];

    if (data_parsed.$sharer_user_id)
      utmData.$sharer_user_id = data_parsed.$sharer_user_id;
    if (data_parsed.$share_link_id)
      utmData.$share_link_id = data_parsed.$share_link_id;
    if (data_parsed.$share_created_date)
      utmData.$share_created_date = data_parsed.$share_created_date;
  }

  if (Object.keys(utmData).length > 0) {
    const utmDataJson = JSON.stringify(utmData);
    localStorage.setItem(BRANCH_ATTRIBUTION_STORAGE_KEY, utmDataJson);
    console.info('branch.saveUTM', BRANCH_ATTRIBUTION_STORAGE_KEY, utmDataJson);
  }
};

/**
 * Get utm parameters(utmKeys) from url.
 * */
export const getUTMParametersFromUrl = (searchStr?: string) => {
  const urlSearchParams = new URLSearchParams(
    searchStr ?? window.location.search
  );
  const utmParams: UTM_PARAMETERS_SCHEMA = {};

  utmKeys.forEach(key => {
    const utmValue = urlSearchParams.get(key);

    if (utmValue) {
      utmParams[key] = utmValue;
    }
  });

  return utmParams;
};

/**
 * Get utm parameters(utmKeys) from local storage and return only available utm parameters.
 * */
export const getUTMParameters = () => {
  const branchAttributesStr = localStorage.getItem(BRANCH_ATTRIBUTION_STORAGE_KEY);
  const branchParameters: Record<string, string> = branchAttributesStr
    ? JSON.parse(branchAttributesStr)
    : {};
  const utmParameters: Record<string, string> = {};

  utmKeys.forEach(key => {
    const utmValue = branchParameters[key];

    if (utmValue !== undefined && utmValue !== null) {
      utmParameters[key] = utmValue;
    }
  });

  return utmParameters as UTM_PARAMETERS_SCHEMA;
};

/**
 * clear local storage timestamps so we can reshow the journey on the same url types
 */
export const clearLocalStorageDismissal = () => {
  try {
    const savedBranchData = localStorage.getItem(
      'BRANCH_WEBSDK_KEYjourneyDismissals'
    );

    const dismissals = savedBranchData && JSON.parse(savedBranchData);

    if (dismissals && dismissals['5ce789a947e87c3451981d86']) {
      delete dismissals['5ce789a947e87c3451981d86'];
      localStorage.setItem(
        'BRANCH_WEBSDK_KEYjourneyDismissals',
        JSON.stringify(dismissals)
      );
    } else if (dismissals && dismissals['5cdcfface9007205bbcbec16']) {
      delete dismissals['5cdcfface9007205bbcbec16'];
      localStorage.setItem(
        'BRANCH_WEBSDK_KEYjourneyDismissals',
        JSON.stringify(dismissals)
      );
    }
  } catch (error) {
    console.warn(error);
  }
};

const searchEngines = [
  '360.cn',
  'alice.com',
  'aliceadsl.fr',
  'alltheweb.com',
  'altavista.com',
  'aol.com',
  'ask.com',
  'search.aol.fr',
  'alicesuche.aol.de',
  'search.auone.jp',
  'isearch.avg.com',
  'search.babylon.com',
  'baidu.com',
  'biglobe.ne.jp',
  'bing.com',
  'search.centrum.cz',
  'search.comcast.net',
  'search.conduit.com',
  'daum.net',
  'ekolay.net',
  'eniro.se',
  'go.mail.ru',
  'goo.ne.jp',
  'search.incredimail.com',
  'kvasir.no',
  'bing.com',
  'lycos.com',
  'search.lycos.de',
  'mamma.com',
  'msn.com',
  'money.msn.com',
  'local.msn.com',
  'mynet.com',
  'najdi.si',
  'naver.com',
  'search.netscape.com',
  'szukaj.onet.pl',
  'ozu.es',
  'rakuten.co.jp',
  'rambler.ru',
  'search-results.com',
  'search.smt.docomo.ne.jp',
  'sesam.no',
  'seznam.cz',
  'sogou.com',
  'szukacz.pl',
  'buscador.terra.com.br',
  'search.tut.by',
  'search.ukr.net',
  'search.virgilio.it',
  'voila.fr',
  'wp.pl',
  'yahoo.com',
  'yahoo.cn',
  'yahoo.com',
  'google.com',
  'yandex.com',
  'yandex.ru',
  'yam.com'
];

const getReferrer = () => {
  const a = document.createElement('a');
  a.href = document.referrer;

  let domain = a.hostname;
  let medium = 'referral';

  if (domain.startsWith('m.')) {
    domain = domain.replace(/^m\./, '');
  } else {
    domain = domain.replace(/^www\./, '');
  }

  if (domain.startsWith('insighttimer.com')) {
    domain = '(direct)';
    medium = '(none)';
  } else if (domain.startsWith('google.')) {
    domain = 'google';
    medium = 'organic';
  } else if (domain.startsWith('yahoo.')) {
    domain = 'yahoo';
    medium = 'organic';
  } else if (domain.startsWith('bing.')) {
    domain = 'bing';
    medium = 'organic';
  } else if (domain.startsWith('duckduckgo.')) {
    domain = 'duckduckgo';
    medium = 'organic';
  } else if (searchEngines.includes(domain)) {
    medium = 'organic';
  }

  return {
    domain,
    medium
  };
};

export const analyticsQuery = (tag: string) => {
  const savedBranchData = localStorage.getItem(BRANCH_ATTRIBUTION_STORAGE_KEY);
  const branchData = savedBranchData && JSON.parse(savedBranchData);
  const data: Record<string, string | number> = {};
  const referrer = getReferrer();

  data['~channel'] = (branchData && branchData.utm_source) || referrer.domain;
  data['~feature'] = (branchData && branchData.utm_medium) || referrer.medium;

  if (branchData && branchData.utm_campaign) {
    data['~campaign'] = branchData.utm_campaign;
  }

  if (branchData && branchData.utm_content) {
    data['~keyword'] = branchData.utm_content;
  }

  if (branchData && branchData.utm_term) {
    data.tags = branchData.utm_term;
  }

  const abTestBranchTag = sessionStorage.getItem('abtest_branch_tag');

  return (
    queryString.stringify(data) +
    (tag ? `&tags=${tag}` : '') +
    (abTestBranchTag ? `&tags=${abTestBranchTag}` : '')
  );
};

export const getBranchLinkDetailsFromLocalStore = () => {
  const branchAttributesStr: BranchData = JSON.parse(
    localStorage.getItem(BRANCH_ATTRIBUTION_STORAGE_KEY) ?? '{}'
  );

  return branchAttributesStr;
};

interface SetBranchSeoMetaInfoParams {
  url: string;
  referrerUrl?: string;
}

export const setBranchSeoInfo = ({
  url,
  referrerUrl
}: SetBranchSeoMetaInfoParams) => {
  const branchSetMetaInfo: BranchSeoMetaInfoSchema | null = JSON.parse(
    sessionStorage.getItem(BRANCH_SEO_META_INFO) || '{}'
  );
  const updatedMetaInfo = { ...(branchSetMetaInfo ?? {}) };

  if (!updatedMetaInfo.seo_first_touch_url) {
    updatedMetaInfo.seo_first_touch_url = url;
    updatedMetaInfo.seo_first_touch_timestamp = new Date().getTime();
    updatedMetaInfo.seo_first_touch_referrer_url =
      referrerUrl || document.referrer || undefined;
  }

  updatedMetaInfo.seo_last_touch_url = url;
  updatedMetaInfo.seo_last_touch_timestamp = new Date().getTime();

  sessionStorage.setItem(BRANCH_SEO_META_INFO, JSON.stringify(updatedMetaInfo));

  return updatedMetaInfo;
};

export const getBranchSeoInfo = () => {
  const branchSetMetaInfo: BranchSeoMetaInfoSchema =
    JSON.parse(sessionStorage.getItem(BRANCH_SEO_META_INFO) || '{}') ?? {};

  return branchSetMetaInfo;
};
